/**
 * MODULE: domain/pages/frontend/utils/pageTasksSyncUtils.ts
 * PURPOSE: Tag helpers and sorting for page-linked CRM tasks sync blocks
 */

import type { Task } from '@shared/lib/frontend/api/taskApi';

export const PAGE_TASK_SYNC_TAG_PREFIX = 'pages:page:';

export function buildPageTaskSyncTag(pageId: string): string {
  return `${PAGE_TASK_SYNC_TAG_PREFIX}${pageId}`;
}

export function taskHasPageSyncTag(task: Task, pageId: string): boolean {
  const tag = buildPageTaskSyncTag(pageId);
  return Array.isArray(task.tags) && task.tags.includes(tag);
}

export function isTaskComplete(task: Task): boolean {
  return task.status === 'done' || task.status === 'cancelled' || task.completed;
}

export function filterPageTasks(
  tasks: Task[],
  pageId: string,
  showCompleted: boolean
): Task[] {
  return tasks.filter((task) => {
    if (!taskHasPageSyncTag(task, pageId)) return false;
    if (showCompleted) return true;
    return !isTaskComplete(task);
  });
}

const PRIORITY_ORDER: Record<Task['priority'], number> = {
  urgent: 0,
  high: 1,
  medium: 2,
  low: 3,
};

export function sortPageTasks(tasks: Task[]): Task[] {
  return [...tasks].sort((a, b) => {
    const aDone = isTaskComplete(a);
    const bDone = isTaskComplete(b);
    if (aDone !== bDone) return aDone ? 1 : -1;

    const priorityDiff = PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];
    if (priorityDiff !== 0) return priorityDiff;

    const aDue = a.due_date ? Date.parse(a.due_date) : Number.POSITIVE_INFINITY;
    const bDue = b.due_date ? Date.parse(b.due_date) : Number.POSITIVE_INFINITY;
    if (aDue !== bDue) return aDue - bDue;

    return a.title.localeCompare(b.title);
  });
}

export function formatTaskDueDate(dueDate: string | undefined): string | null {
  if (!dueDate) return null;
  const parsed = new Date(dueDate);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function taskPriorityLabel(priority: Task['priority']): string {
  switch (priority) {
    case 'urgent':
      return 'Urgent';
    case 'high':
      return 'High';
    case 'low':
      return 'Low';
    default:
      return 'Medium';
  }
}

export function taskPriorityBadgeClass(priority: Task['priority']): string {
  switch (priority) {
    case 'urgent':
      return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200';
    case 'high':
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200';
    case 'low':
      return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
    default:
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200';
  }
}
